import React, { useState } from "react";
import { Loader2, PenLine, RefreshCw, Save, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { api } from "../lib/api";
import { toast } from "sonner";

export const THESIS_SECTIONS = [
  { key: "the_read", label: "The read", hint: "What the setup is, in plain words" },
  { key: "whats_underneath", label: "What's underneath it", hint: "Fundamentals, catalyst, sector tailwind" },
  { key: "what_breaks", label: "What breaks it", hint: "Concrete triggers: price level, catalyst date, market condition" },
  { key: "market_context", label: "Market context", hint: "What the broader tape is doing" },
];

const pickSections = (d) => Object.fromEntries(THESIS_SECTIONS.map((s) => [s.key, d?.[s.key] || ""]));

// Desk-side thesis editor. `value` holds the four sections (or null when none written yet).
export default function ThesisEditor({ alert, value, onChange }) {
  const [busy, setBusy] = useState(null);
  const sym = alert.symbol;

  const write = async () => {
    setBusy("write");
    try {
      const r = await api.post(`/alerts/admin/${alert.id}/thesis/generate`);
      onChange(pickSections(r.data));
      toast.success(`Thesis drafted for ${sym}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Thesis generation failed — retry");
    }
    setBusy(null);
  };

  const save = async () => {
    setBusy("save");
    try {
      await api.put(`/alerts/admin/${alert.id}/thesis`, value);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(null);
  };

  if (!value) {
    return (
      <div className="mt-4 flex items-center gap-3 rounded-md border border-dashed border-mb-border px-4 py-3" data-testid={`thesis-empty-${sym}`}>
        <PenLine className="w-4 h-4 text-neutral-500" />
        <span className="text-sm text-neutral-600 flex-1">No thesis yet. It publishes with the alert and locks on approve.</span>
        <Button size="sm" onClick={write} disabled={!!busy} className="bg-mb-emerald hover:bg-mb-emerald-dark text-white rounded-md" data-testid={`thesis-write-btn-${sym}`}>
          {busy === "write" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
          {busy === "write" ? "Writing…" : "Write thesis"}
        </Button>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-mb-border p-4 space-y-3" data-testid={`thesis-editor-${sym}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="label-uppercase text-mb-emerald">Entry thesis · draft</div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={write} disabled={!!busy} className="rounded-md" data-testid={`thesis-regenerate-btn-${sym}`}>
            {busy === "write" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Rewrite
          </Button>
          <Button size="sm" variant="outline" onClick={save} disabled={!!busy} className="rounded-md" data-testid={`thesis-save-btn-${sym}`}>
            {busy === "save" ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Save draft
          </Button>
        </div>
      </div>
      {THESIS_SECTIONS.map((s) => (
        <label key={s.key} className="block">
          <span className="text-xs font-semibold text-neutral-800">{s.label}</span>
          <span className="text-[11px] text-neutral-500 ml-2">{s.hint}</span>
          <Textarea
            value={value[s.key]}
            onChange={(e) => onChange({ ...value, [s.key]: e.target.value })}
            rows={4}
            className="mt-1 text-sm"
            data-testid={`thesis-field-${s.key}-${sym}`}
          />
        </label>
      ))}
      <p className="text-[11px] text-neutral-500">Approve & Broadcast locks this text. Subscribers see it on the stock page; the email carries "The read".</p>
    </div>
  );
}

export { pickSections };
